import React, { useState } from 'react';
import { ReservationService } from '../services/ReservationService';
import { AuthService } from '../services/AuthService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Lock, Eye, EyeOff } from 'lucide-react';
import useRequisitosSenha from '../hooks/useRequisitosSenha';
import PainelRequisitosSenha from './PainelRequisitosSenha';

interface TrocarSenhaFormProps {
  idcliente: number;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onLogout?: () => void; // Callback para notificar o componente pai sobre o logout
}

const TrocarSenhaForm: React.FC<TrocarSenhaFormProps> = ({ idcliente, onSuccess, onError, onLogout }) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  // {|INICIO| 27/07/2026 às 18:36 - CENTRALIZAR REQUISITOS DE SENHA DA TROCA LOGADA EM RECURSO COMPARTILHADO}
  const {
    requisitosSenha,
    senhaAtendeRequisitos,
    forcaSenha,
    classeCorBarraForca,
    classeCorTextoForca
  } = useRequisitosSenha(novaSenha);
  // {|FIM| 27/07/2026 às 18:36 - CENTRALIZAR REQUISITOS DE SENHA DA TROCA LOGADA EM RECURSO COMPARTILHADO}

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // {|INICIO| 22/07/2026 às 16:43 - TROCA DE SENHA: ALINHAR VALIDACOES DO FORMULARIO COM O BACKEND}
    // Validação local
    if (!senhaAtual.trim()) {
      setMessage({ type: 'danger', text: t('app.currentPasswordRequired') });
      return;
    }

    if (!novaSenha.trim()) {
      setMessage({ type: 'danger', text: t('app.newPasswordRequired') });
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setMessage({ type: 'danger', text: t('app.passwordsDoNotMatch') });
      return;
    }

    // {|INICIO| 27/07/2026 às 18:30 - ALINHAR VALIDACAO DA TROCA LOGADA COM O FLUXO DE RESET}
    // Validação anterior mantida para histórico conforme regra do projeto:
    // if (novaSenha.length < 8) {
    //   setMessage({ type: 'danger', text: t('app.passwordMinLength') });
    //   return;
    // }
    if (!senhaAtendeRequisitos) {
      setMessage({ type: 'danger', text: t('auth.passwordRequirementsNotMet') });
      return;
    }
    // {|FIM| 27/07/2026 às 18:30 - ALINHAR VALIDACAO DA TROCA LOGADA COM O FLUXO DE RESET}

    setLoading(true);

    try {
      const response = await ReservationService.trocarSenha(idcliente, senhaAtual, novaSenha, confirmarSenha);
      
      if (response.status === 'success') {
        setMessage({ type: 'success', text: response.message });
        // Limpar os campos após sucesso
        setSenhaAtual('');
        setNovaSenha('');
        setConfirmarSenha('');
        
        // Executar logout após 2 segundos para mostrar a mensagem de sucesso
        setTimeout(async () => {
          try {
            await AuthService.logout();
            if (onLogout) onLogout();
          } catch (error) {
            console.error('Erro ao fazer logout:', error);
          }
        }, 2000);
        
        if (onSuccess) onSuccess();
      } else {
        setMessage({ type: 'danger', text: response.message });
        if (onError) onError(response.message);
      }
      // {|FIM| 22/07/2026 às 16:43 - TROCA DE SENHA: ALINHAR VALIDACOES DO FORMULARIO COM O BACKEND}
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('app.changePasswordError');
      setMessage({ type: 'danger', text: errorMessage });
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="trocar-senha-form">
      <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{t('app.changePassword')}</h3>
      
      {message && (
        <div className={`p-4 mb-4 rounded ${
          message.type === 'success' 
            ? theme === 'dark' 
              ? 'bg-green-900/20 border border-green-700 text-green-300' 
              : 'bg-green-100 border border-green-400 text-green-700'
            : theme === 'dark'
              ? 'bg-red-900/20 border border-red-700 text-red-300'
              : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Campo de usuário oculto para acessibilidade e gerenciadores de senha */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          style={{ display: 'none' }}
        />
        <div>
          <label htmlFor="senhaAtual" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
            {t('app.currentPassword')}
          </label>
          <div className="relative">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type={showSenhaAtual ? 'text' : 'password'}
              id="senhaAtual"
              autoComplete="current-password"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              disabled={loading}
              className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 disabled:bg-gray-800'
                  : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowSenhaAtual(!showSenhaAtual)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showSenhaAtual ? (
                <EyeOff className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <Eye className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </button>
          </div>
        </div>
        
        <div>
          <label htmlFor="novaSenha" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
            {t('app.newPassword')}
          </label>
          {/* {|INICIO| 27/07/2026 às 18:36 - SUBSTITUIR BLOCO LOCAL DA TROCA LOGADA POR PAINEL COMPARTILHADO} */}
          <PainelRequisitosSenha
            requisitosSenha={requisitosSenha}
            forcaSenha={forcaSenha}
            classeCorBarraForca={classeCorBarraForca}
            classeCorTextoForca={classeCorTextoForca}
            exibirForcaSenha={Boolean(novaSenha)}
          />
          {/* {|FIM| 27/07/2026 às 18:36 - SUBSTITUIR BLOCO LOCAL DA TROCA LOGADA POR PAINEL COMPARTILHADO} */}
          <div className="relative">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type={showNovaSenha ? 'text' : 'password'}
              id="novaSenha"
              autoComplete="new-password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              disabled={loading}
              className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 disabled:bg-gray-800'
                  : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowNovaSenha(!showNovaSenha)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showNovaSenha ? (
                <EyeOff className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <Eye className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </button>
          </div>
        </div>
        
        <div>
          <label htmlFor="confirmarSenha" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
            {t('app.confirmNewPassword')}
          </label>
          <div className="relative">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type={showConfirmarSenha ? 'text' : 'password'}
              id="confirmarSenha"
              autoComplete="new-password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              disabled={loading}
              className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 disabled:bg-gray-800'
                  : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showConfirmarSenha ? (
                <EyeOff className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <Eye className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </button>
          </div>
        </div>
        
        <button 
          type="submit" 
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('app.changing')}
            </>
          ) : (
            t('app.changePassword')
          )}
        </button>
      </form>
    </div>
  );
};

export default TrocarSenhaForm;
